import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchApplications, fetchApplicationProducts } from '../services/ApplicationProductsService';
import { useProducts } from './useProducts';
import { useSubscriptionStatus } from './useSubscriptionStatus';
import { ApplicationWithPlans, Product } from '../types';
import { Application } from '../../../accounts-management/applications/types';

export const useApplicationProducts = () => {
    const {
        data: applications,
        isLoading: applicationsLoading,
    } = useQuery({
        queryKey: ['applications'],
        queryFn: fetchApplications,
    });

    const {
        data: applicationProducts,
        isLoading: mappingsLoading,
    } = useQuery({
        queryKey: ['applicationProducts'],
        queryFn: () => fetchApplicationProducts(),
    });

    const { products, isLoading: productsLoading } = useProducts();
    const { statusByProductId, loading: subscriptionsLoading } = useSubscriptionStatus();

    const applicationsWithPlans = useMemo<ApplicationWithPlans[]>(() => {
        if (!applications || !applicationProducts || !products) return [];

        const productMap = new Map(products.map(p => [p.id, p]));

        const grouped = new Map<string, Product[]>();
        for (const ap of applicationProducts) {
            if (ap.applicationProductStatus === 'INACTIVE') continue;
            const product = productMap.get(ap.productId);
            if (!product || product.status !== 'Active') continue;

            const list = grouped.get(ap.applicationId) || [];
            list.push({ ...product, _position: ap.position } as Product & { _position?: number });
            grouped.set(ap.applicationId, list);
        }

        return applications
            .filter(app => grouped.has(app.id))
            .map((app: Application): ApplicationWithPlans => {
                const appProducts = grouped.get(app.id) || [];
                // Sort by position first, then by lowest price ascending
                appProducts.sort((a, b) => {
                    const posA = (a as Product & { _position?: number })._position ?? 999;
                    const posB = (b as Product & { _position?: number })._position ?? 999;
                    if (posA !== posB) return posA - posB;
                    const priceA = a.prices?.items?.[0]?.amount ?? 0;
                    const priceB = b.prices?.items?.[0]?.amount ?? 0;
                    return priceA - priceB;
                });

                const subscribedProduct = appProducts.find(p => statusByProductId[p.id]?.isSubscribed);

                return {
                    application: app,
                    products: appProducts,
                    subscribedProductId: subscribedProduct?.id,
                };
            });
    }, [applications, applicationProducts, products, statusByProductId]);

    // Products not mapped to any application (fallback display)
    const unmappedProducts = useMemo(() => {
        if (!products || !applicationProducts) return [];
        const mappedProductIds = new Set(applicationProducts.map(ap => ap.productId));
        return products.filter(p => !mappedProductIds.has(p.id) && p.status === 'Active');
    }, [products, applicationProducts]);

    const isLoading = applicationsLoading || mappingsLoading || productsLoading || subscriptionsLoading;

    return {
        applicationsWithPlans,
        unmappedProducts,
        statusByProductId,
        isLoading,
    };
};
